import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import {
    Animated,
    Platform,
    Pressable,
    StyleProp,
    View,
    ViewStyle,
} from 'react-native';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { COLOR } from 'utils/colors';
import usePrevious from 'utils/hooks/usePrevious';
import { isWeb } from 'utils/responsiveness';

import styles from './styles';

type Props = {
    value: boolean;
    onValueChange: (value: boolean) => void;
    style?: StyleProp<ViewStyle>;
    accessibilityLabelWhenToggledOn?: string;
    accessibilityLabelWhenToggledOff?: string;
};

const ToggleButton = ({
    value,
    onValueChange,
    style,
    accessibilityLabelWhenToggledOn,
    accessibilityLabelWhenToggledOff,
}: Props) => {
    const thumbPosition = useRef(new Animated.Value(value ? 1 : 0)).current;
    const toggleRef = useRef<View>(null);
    const prevValue = usePrevious(value);

    const handleToggleAnimation = useCallback(
        (newValue) => {
            Animated.timing(thumbPosition, {
                toValue: newValue ? 1 : 0,
                duration: 200,
                useNativeDriver: !isWeb,
            }).start(() => {
                if (Platform.OS !== 'android' || prevValue) {
                    setTimeout(() => {
                        setAccessibilityFocusTo(toggleRef);
                    });
                }
            });
        },
        [thumbPosition, prevValue],
    );

    const toggleSwitch = useCallback(() => {
        const newValue = !value;

        onValueChange(newValue);
    }, [value, onValueChange]);

    const onAccessibilityAction = useCallback(
        (event) => {
            if (event?.nativeEvent?.actionName === 'activate') {
                toggleSwitch();
            }
        },
        [toggleSwitch],
    );

    const accessibilityLabel = useMemo(() => {
        return value
            ? accessibilityLabelWhenToggledOn
            : accessibilityLabelWhenToggledOff;
    }, [
        value,
        accessibilityLabelWhenToggledOn,
        accessibilityLabelWhenToggledOff,
    ]);

    const accessibilityProps = isWeb
        ? {
              accessibilityRole: 'switch' as const,
              'aria-checked': value,
              accessibilityLabel,
          }
        : {
              accessibilityRole: 'switch' as const,
              accessibilityLabel,
          };

    useEffect(() => {
        if (prevValue !== value) {
            handleToggleAnimation(value);
        }
    }, [value, prevValue, handleToggleAnimation]);

    return (
        <Pressable
            ref={toggleRef}
            onPress={toggleSwitch}
            accessible
            {...accessibilityProps}
            accessibilityActions={[{ name: 'activate' }]}
            onAccessibilityAction={onAccessibilityAction}
            accessibilityState={{ checked: value }}
            {...(Platform.OS === 'ios' && {
                accessibilityValue: { text: value ? 'On' : 'Off' },
            })}
            style={[styles.container, style]}
        >
            <View
                style={{
                    ...styles.track,
                    backgroundColor: value ? COLOR.primary : COLOR.LIGHT_GREY,
                }}
            >
                <Animated.View
                    style={{
                        ...styles.thumb,
                        transform: [
                            {
                                translateX: thumbPosition.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: [0, 20],
                                }),
                            },
                        ],
                    }}
                />
            </View>
        </Pressable>
    );
};

export default ToggleButton;
